import type { ImporterDocumentRow } from '../types';
import { normalizeDateToYyyyMmDd, sanitizeAmount } from './transforms';
import type { ParseContext, ParseRule } from './types';

const DEFAULT_DATE_FIELDS = ['issuedDate', 'dueDate'];
const DEFAULT_AMOUNT_FIELDS = ['amount'];
const DEFAULT_REQUIRED_KEYS = ['folio', 'stakeholderIdentifier'];

function getInternalKey(
  header: string,
  columnMapping: Record<string, string>,
): string | null {
  const trimmed = String(header).trim();
  const key =
    columnMapping[trimmed] ?? columnMapping[trimmed.toLowerCase()] ?? null;
  return key ?? null;
}

function getRecordValue(
  record: Record<string, unknown>,
  columnName: string,
): unknown {
  const want = String(columnName).trim().toLowerCase();
  for (const key of Object.keys(record)) {
    if (key.trim().toLowerCase() === want) return record[key];
  }
  return undefined;
}

function hasRequiredKey(
  row: Record<string, unknown>,
  requiredKeys: string[],
): boolean {
  for (const k of requiredKeys) {
    const v = row[k];
    if (v != null && String(v).trim() !== '') return true;
  }
  return false;
}

export function applyRuleToRecords(
  records: Record<string, unknown>[],
  rule: ParseRule,
  context: ParseContext,
): ImporterDocumentRow[] {
  const { businessIdentifier } = context;
  const dateFields = rule.dateFields ?? DEFAULT_DATE_FIELDS;
  const amountFields = rule.amountFields ?? DEFAULT_AMOUNT_FIELDS;
  const requiredKeys = rule.requiredKeysForRow ?? DEFAULT_REQUIRED_KEYS;
  const mapping = rule.columnMapping;

  const rows: ImporterDocumentRow[] = [];

  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    if (!record || typeof record !== 'object') continue;

    const doc: Record<string, unknown> = {
      businessIdentifier,
      folio: '',
      stakeholderIdentifier: '',
      issuedDate: '',
      amount: 0,
    };

    for (const header of Object.keys(record)) {
      const internalKey = getInternalKey(header, mapping);
      if (!internalKey || internalKey === 'businessIdentifier') continue;
      const raw = record[header];
      doc[internalKey] = raw != null ? raw : '';
    }

    for (const key of dateFields) {
      if (doc[key] !== undefined) {
        doc[key] = normalizeDateToYyyyMmDd(doc[key]);
      }
    }
    for (const key of amountFields) {
      if (doc[key] !== undefined) {
        doc[key] = sanitizeAmount(doc[key]);
      }
    }

    if (rule.codeToType) {
      const rawCode = getRecordValue(record, rule.codeToType.column);
      const codeStr = rawCode != null ? String(rawCode).trim() : '';
      doc.type = rule.codeToType.map[codeStr] ?? (codeStr || undefined);
    }

    const folio = String(doc.folio ?? '').trim();
    doc.id = `${folio}-${businessIdentifier}-${i}`;

    if (!hasRequiredKey(doc, requiredKeys)) continue;

    rows.push({
      id: doc.id as string,
      folio: folio || '',
      businessIdentifier,
      stakeholderIdentifier:
        String(doc.stakeholderIdentifier ?? '').trim() || '',
      stakeholderName:
        doc.stakeholderName != null
          ? String(doc.stakeholderName).trim() || undefined
          : undefined,
      clientIdentifier: businessIdentifier,
      issuedDate: (doc.issuedDate as string) || '',
      receiptDate:
        doc.receiptDate != null
          ? String(doc.receiptDate).trim() || undefined
          : undefined,
      amount: (doc.amount as number) ?? 0,
      type: doc.type != null ? String(doc.type).trim() || undefined : undefined,
      dueDate:
        doc.dueDate != null
          ? String(doc.dueDate).trim() || undefined
          : undefined,
    });
  }

  return rows;
}
